import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, MenuItem } from '@mui/material';
import { addDays, isSameDay, startOfDay } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { connect, useDispatch } from 'react-redux';
import * as yup from 'yup';
import { FormAutocomplete, FormDatePicker, FormSelect, FormTextField } from '../../../common/form';
import { CustomerDto, PricelistClient, PricelistDto } from '../../../common/PricePrgClient';
import { ProgressButton } from '../../../common/ProgressButton';
import { RootState } from '../../../store';
import { requiredDateSchema } from '../../../utils';
import { getAppSettings } from '../../settings/redux/settingsSelectors';
import { pricePrgError } from '../../ui/redux/uiSlices';
import { getCurrentUser } from '../../user/redux/userSelectors';
import { getCustomerUi, getPurchasers } from '../redux/customerSelectors';
import { sendPricelistToEmail } from '../redux/customerSlices';

const mapStateToProps = (state: RootState) => ({
    currentUserEmail: getCurrentUser(state)?.username,
    customerUi: getCustomerUi(state),
    mailSendouts: getAppSettings(state).mailSendouts,
    purchasers: getPurchasers(state)
});

type OwnProps = {
    selectedCustomer: CustomerDto;
    closeDialog(): void;
};

type SendPricelistToMailForm = {
    mailSendoutId: string;
    validAt: Date;
    email: string;
    pricelistId: number;
    purchaser?: string | null;
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const schema: yup.SchemaOf<SendPricelistToMailForm> = yup.object().shape({
    mailSendoutId: yup.string().required().label('Mail sendout'),
    email: yup.string().email().required().label('Email'),
    validAt: requiredDateSchema.label('Valid at'),
    pricelistId: yup.number().required().label('Pricelist'),
    purchaser: yup.string().nullable().optional().label('Purchaser')
});

const pricelistClient = new PricelistClient();

const SendPricelistToMailDialog = ({
    closeDialog,
    selectedCustomer,
    currentUserEmail,
    customerUi,
    mailSendouts,
    purchasers
}: Props) => {
    const dispatch = useDispatch();
    const minDate = useMemo(() => startOfDay(addDays(new Date(), 1)), []);
    const [pricelists, setPricelists] = useState<PricelistDto[]>([]);
    const [validFroms, setValidFroms] = useState<Date[]>([]);
    const isSending = customerUi.isSendingPricelistMail;

    const { control, handleSubmit, setValue, watch, reset, getValues } = useForm<SendPricelistToMailForm>({
        resolver: yupResolver(schema),
        defaultValues: useMemo(
            () => ({
                pricelistId: pricelists.length === 1 ? pricelists[0].pricelistId : -1,
                validAt: minDate,
                email: currentUserEmail
            }),
            [pricelists, minDate, currentUserEmail]
        )
    });

    const selectedPricelistId = watch('pricelistId');

    useEffect(() => {
        if (!selectedPricelistId) {
            return;
        }
        async function fetchPricelists() {
            try {
                const pls = await pricelistClient.getPricelistsForCustomer(selectedCustomer.customerId);
                if (pls.length === 1) {
                    setValue('pricelistId', pls[0].pricelistId);
                }
                setPricelists(pls);
            } catch (err) {
                dispatch(pricePrgError(err));
            }
        }

        fetchPricelists();
    }, [setValue, selectedCustomer, dispatch, selectedPricelistId]);

    useEffect(() => {
        if (!selectedPricelistId) {
            return;
        }

        setValidFroms([]);
        reset({
            ...getValues(),
            validAt: undefined
        });

        async function fetchPricelistPeriods() {
            try {
                const plps = await pricelistClient.getPricelistsPeriodsForPricelists([selectedPricelistId]);
                setValidFroms(plps.map(plp => new Date(plp.validFrom)));
            } catch (err) {
                dispatch(pricePrgError(err));
            }
        }

        fetchPricelistPeriods();
    }, [selectedPricelistId, dispatch, getValues, reset]);

    const submitForm = (form: SendPricelistToMailForm) => {
        dispatch(
            sendPricelistToEmail.request({
                mailSendoutId: form.mailSendoutId,
                pricelistId: form.pricelistId,
                validAt: form.validAt.toISOString(),
                email: form.email,
                customerId: selectedCustomer.customerId,
                purchaser: form.purchaser ?? undefined
            })
        );
    };

    const shouldDisableDate = (date: Date | null) => {
        if (date == null) {
            return false;
        }

        return validFroms.every(validFrom => !isSameDay(validFrom, date));
    };

    return (
        <Dialog open={true}>
            {/* Todo: Check if Paper needs the sx style */}
            <DialogTitle>Send customer pricelist to email</DialogTitle>
            <DialogContent>
                <form id="sendPricelistToMailForm" onSubmit={handleSubmit(submitForm)}>
                    <Grid sx={{ mt: 1 }} container direction="column" spacing={2}>
                        <Grid item xs={6}>
                            <FormSelect label="Mail sendout" name="mailSendoutId" control={control}>
                                {mailSendouts.map(s => (
                                    <MenuItem key={s.mailSendoutId} value={s.mailSendoutId}>
                                        {s.name}
                                    </MenuItem>
                                ))}
                            </FormSelect>
                        </Grid>
                        <Grid item xs={12}>
                            <FormTextField control={control} name="email" label="Email" fullWidth />
                        </Grid>
                        <Grid item xs={10}>
                            <FormSelect label="Pricelist" name="pricelistId" control={control}>
                                {pricelists.map(pl => (
                                    <MenuItem key={pl.pricelistId} value={pl.pricelistId}>
                                        {pl.name}
                                    </MenuItem>
                                ))}
                            </FormSelect>
                        </Grid>
                        <Grid item xs={10}>
                            <FormDatePicker
                                name="validAt"
                                label="Valid at"
                                control={control}
                                inputFormat="yyyy-MM-dd"
                                shouldDisableDate={shouldDisableDate}
                                disabled={validFroms.length === 0}
                                InputProps={{ readOnly: true }}
                            />
                        </Grid>
                        <Grid item xs={10}>
                            <FormAutocomplete
                                control={control}
                                name="purchaser"
                                label="Purchaser"
                                options={purchasers}
                            />
                        </Grid>
                    </Grid>
                </form>
            </DialogContent>
            <DialogActions>
                <Button onClick={closeDialog}>Cancel</Button>
                <ProgressButton
                    color="primary"
                    type="submit"
                    form="sendPricelistToMailForm"
                    isLoading={isSending}
                >
                    Send
                </ProgressButton>
            </DialogActions>
        </Dialog>
    );
};

export default connect(mapStateToProps)(SendPricelistToMailDialog);
